import { fileType } from "@agentcall/core/type"
import { type ChangeEvent, type DragEvent, useEffect, useRef, useState } from "react"
import { FileText, Trash2 } from "react-feather"
import { UPLOAD_ACCEPT } from "@/api/client/file"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

// ファイルインデックスに基づく固定タイトル（0: 賃貸借契約書, 1: 重要事項説明書）
const FIXED_TITLE_BY_INDEX: Record<number, string> = {
  0: fileType.TENANT_FILE_TITLE_RENTAL_CONTRACT,
  1: fileType.TENANT_FILE_TITLE_IMPORTANT_MATTERS,
}
const DEFAULT_OTHER_TITLE = "その他"

const IMAGE_FILE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|bmp|svg)$/i
const isImageFile = (fileType: string | null | undefined, fileName?: string | null) =>
  fileType?.startsWith("image/") || (!!fileName && IMAGE_FILE_EXTENSION_REGEX.test(fileName))

interface AttachmentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: "add" | "edit"
  /** ファイルのインデックス（0始まり）。1,2枚目はタイトル固定 */
  fileIndex?: number
  initialData?: {
    title: string
    imageUrl?: string
    fileUrl?: string
    fileName?: string | null
    fileType?: string | null
  }
  onSubmit: (data: { title: string; file?: File }) => void | Promise<void>
  onDelete?: () => void | Promise<void>
  isSubmitting?: boolean
}

export function AttachmentModal({
  open,
  onOpenChange,
  mode,
  fileIndex,
  initialData,
  onSubmit,
  onDelete,
  isSubmitting,
}: AttachmentModalProps) {
  // 1,2枚目の場合は固定タイトル
  const fixedTitle = fileIndex !== undefined ? FIXED_TITLE_BY_INDEX[fileIndex] : undefined
  const isTitleFixed = fixedTitle !== undefined

  const [title, setTitle] = useState(fixedTitle ?? initialData?.title ?? "")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialData?.fileUrl || initialData?.imageUrl || null
  )
  const [isImageRemoved, setIsImageRemoved] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  // 誤操作で消さないよう、削除は 2 回押して確定する
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selectedFileName = selectedFile?.name ?? initialData?.fileName ?? initialData?.title
  const isPreviewImage = selectedFile
    ? isImageFile(selectedFile.type, selectedFile.name)
    : isImageFile(initialData?.fileType, initialData?.fileName) ||
      (!!initialData?.imageUrl && !initialData?.fileUrl)

  // モーダルが開くたびにタイトルを更新
  useEffect(() => {
    if (open) {
      setTitle(fixedTitle ?? initialData?.title ?? "")
      setPreviewUrl(initialData?.fileUrl || initialData?.imageUrl || null)
      setSelectedFile(null)
      setIsImageRemoved(false)
      setIsDragging(false)
      setIsConfirmingDelete(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }, [open, fixedTitle, initialData?.title, initialData?.imageUrl, initialData?.fileUrl])

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      selectFile(file)
    }
  }

  const selectFile = (file: File) => {
    setSelectedFile(file)
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    setIsImageRemoved(false)
  }

  const handleFileDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) {
      selectFile(file)
    }
  }

  const handleFileDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    if (event.dataTransfer.types.includes("Files")) {
      setIsDragging(true)
    }
  }

  const handleFileDragLeave = (event: DragEvent<HTMLLabelElement>) => {
    const relatedTarget = event.relatedTarget
    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
      return
    }
    setIsDragging(false)
  }

  const handleRemoveImage = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setIsImageRemoved(true)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSubmit = () => {
    const submitTitle = isTitleFixed ? fixedTitle : title.trim() || DEFAULT_OTHER_TITLE
    if (submitTitle && (mode === "edit" || selectedFile)) {
      onSubmit({
        title: submitTitle,
        file: selectedFile || undefined,
      })
      // モーダルのクローズは親コンポーネントの非同期処理完了後に行う
    }
  }

  const handleClose = () => {
    setTitle(initialData?.title || "")
    setSelectedFile(null)
    setPreviewUrl(initialData?.fileUrl || initialData?.imageUrl || null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    onOpenChange(false)
  }

  // 編集モードで画像が削除された場合は新しいファイルが必須
  const needsNewFile = mode === "add" || (mode === "edit" && isImageRemoved)
  const isSubmitDisabled =
    (mode === "edit" && !isTitleFixed && !title.trim()) ||
    (needsNewFile && !selectedFile) ||
    isSubmitting

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[345px] gap-0 p-0 rounded-[10px]">
        <div className="px-5 py-8">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-lg font-semibold text-[#354267]">
              {mode === "add" ? "添付ファイルの追加" : "添付ファイルの編集"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-[5px]">
              <Label htmlFor="title" className="text-sm font-semibold text-slate-600">
                タイトル
              </Label>
              {isTitleFixed ? (
                <div className="h-10 flex items-center px-3 text-sm text-slate-600 bg-slate-100 rounded-md border border-slate-200">
                  {fixedTitle}
                </div>
              ) : (
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={mode === "add" ? DEFAULT_OTHER_TITLE : "タイトルを入力"}
                  className="h-10 border-slate-300 rounded-md px-3 text-sm"
                />
              )}
            </div>

            <div className="space-y-[10px]">
              {previewUrl && (
                <div className="relative w-full h-[109px] rounded-md overflow-hidden bg-slate-100">
                  {isPreviewImage ? (
                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center gap-3 px-4 text-slate-600">
                      <FileText className="size-8 shrink-0 text-slate-400" />
                      <span className="min-w-0 truncate text-sm font-semibold">
                        {selectedFileName || "添付ファイル"}
                      </span>
                    </div>
                  )}
                  {(selectedFile || mode === "edit") && (
                    <Button
                      type="button"
                      onClick={handleRemoveImage}
                      size="sm"
                      variant="destructive"
                      className="absolute top-[5px] right-[5px] h-auto py-1"
                    >
                      <Trash2 className="size-4" />
                      削除
                    </Button>
                  )}
                </div>
              )}

              {mode === "add" && !previewUrl && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={UPLOAD_ACCEPT}
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`flex items-center justify-center w-full h-[109px] rounded-md border-2 border-dashed cursor-pointer transition-colors ${
                      isDragging
                        ? "border-blue-300 bg-blue-50"
                        : "border-slate-300 hover:border-slate-400"
                    }`}
                    onDrop={handleFileDrop}
                    onDragOver={handleFileDragOver}
                    onDragLeave={handleFileDragLeave}
                  >
                    <div className="text-center">
                      <p className="text-sm text-slate-400">ファイルを選択 / ドロップ</p>
                    </div>
                  </label>
                </div>
              )}

              {mode === "edit" && !previewUrl && (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={UPLOAD_ACCEPT}
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`flex items-center justify-center w-full h-[109px] rounded-md border-2 border-dashed cursor-pointer transition-colors ${
                      isDragging
                        ? "border-blue-300 bg-blue-50"
                        : "border-slate-300 hover:border-slate-400"
                    }`}
                    onDrop={handleFileDrop}
                    onDragOver={handleFileDragOver}
                    onDragLeave={handleFileDragLeave}
                  >
                    <div className="text-center">
                      <p className="text-sm text-slate-400">ファイルを選択 / ドロップ</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <DialogFooter className="gap-[10px] sm:justify-end">
              {mode === "edit" && onDelete && (
                <Button
                  variant="destructive"
                  onClick={() => (isConfirmingDelete ? onDelete() : setIsConfirmingDelete(true))}
                  disabled={isSubmitting}
                  className="h-auto py-2 px-4 font-bold text-sm rounded-md sm:mr-auto"
                >
                  {isConfirmingDelete ? "本当に削除する" : "添付ファイルを削除"}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleClose}
                className="h-auto py-2 px-4 border-slate-400 text-[#354267] font-bold text-sm rounded-md"
              >
                キャンセル
              </Button>
              <Button
                onClick={handleSubmit}
                loading={isSubmitDisabled}
                className="h-auto py-2 px-4 bg-[#354267] hover:bg-[#354267]/90 disabled:bg-slate-200 text-white disabled:text-slate-400 font-bold text-sm rounded-md"
              >
                {isSubmitting ? "処理中..." : mode === "add" ? "追加" : "保存"}
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
